'use client';

import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Image from 'next/image';
import { Shield, Gavel, Gamepad2, Wrench } from 'lucide-react';

const features = [
  {
    title: 'Antinuke Protection',
    description: 'Advanced security system that logs all important server events. Monitors joins, leaves, bans, role changes, and channel modifications. Whitelist trusted admins for peace of mind.',
    icon: null,
    IconComponent: Shield,
    color: 'from-red-500 to-red-600',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/20',
  },
  {
    title: 'Moderation Tools',
    description: 'Complete moderation suite with ban, kick, mute, warn, and message management. Automated slowmode, bulk delete, and comprehensive logging keep your server clean and organized.',
    icon: null,
    IconComponent: Gavel,
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/20',
  },
  {
    title: 'Fun & Games',
    description: 'Engage your community with interactive games like Tic-Tac-Toe, 8ball, memes, coin flips, and dice rolls. Keep your server active and entertaining 24/7.',
    icon: null,
    IconComponent: Gamepad2,
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/20',
  },
  {
    title: 'Utility Commands',
    description: 'Powerful utilities including role management, autoresponders, server info, user info, avatar displays, and custom embed creators. Everything you need in one bot.',
    icon: null,
    IconComponent: Wrench,
    color: 'from-green-500 to-green-600',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/20',
  },
];

export function FeaturesShowcase() {
  return (
    <section className="py-24 bg-[#0f0f0f]">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Everything Your Server Needs
          </h2>
          <p className="text-xl text-[#b9bbbe] max-w-2xl mx-auto">
            Yokki combines powerful security, efficient moderation, and engaging entertainment—all in one bot.
          </p>
        </motion.div>

        {/* Features grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className={`h-full backdrop-blur-sm bg-[#1a1a1a]/80 border ${feature.borderColor} hover:bg-[#252525]/80 transition-all duration-300 hover:scale-105 hover:shadow-2xl`}>
                <CardHeader>
                  <CardTitle className="text-2xl text-white">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-[#b9bbbe] text-base leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Stats section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-20 grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-5xl mx-auto"
        >
          <div className="text-center">
            <div className="text-4xl font-bold text-[#5865F2] mb-2">50+</div>
            <div className="text-[#b9bbbe]">Commands Available</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-[#5865F2] mb-2">99.9%</div>
            <div className="text-[#b9bbbe]">Uptime</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-[#5865F2] mb-2">24/7</div>
            <div className="text-[#b9bbbe]">Support Available</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
